import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { Button } from '@/components/ui/button'
import { PortalDocumentBranding } from '@/features/portal/portal-document-branding'
import {
  analyticsAvailable,
  COOKIE_SETTINGS_EVENT,
  flushSignUp,
  type Choice as CookieChoice,
  type Consent,
  isExcludedPath,
  loadAnalytics,
  readConsent,
  saveConsent,
  stopAnalytics,
  stopReferrals,
  trackPageView,
} from '@/lib/analytics'
import { applyPendingReferral } from '@/lib/referral'
import { portalMode } from '@/lib/portal'
import { cn } from '@/lib/cn'

/** The root route: every page, plus the cookie notice and page views (see lib/analytics). */
export function AppRoot() {
  return (
    <>
      {portalMode && <PortalDocumentBranding />}
      {/* Before the page, so on the sign-up and sign-in pages a phone can show it as a strip above
          the form instead of over it. Elsewhere it is fixed to the corner, wherever it sits. */}
      {analyticsAvailable && <CookieNotice />}
      <Outlet />
    </>
  )
}

function isAuthFormPath(pathname: string): boolean {
  return /^\/(register|login)\/?$/.test(pathname)
}

function CookieNotice() {
  const { pathname } = useLocation()
  const excluded = isExcludedPath(pathname)
  // The sign-up and sign-in pages: the notice must never sit over their fields or the submit button.
  const onAuthPage = isAuthFormPath(pathname)
  const [consent, setConsent] = useState<Consent>(readConsent)
  const [reopened, setReopened] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false)
  const [ads, setAds] = useState(consent?.ads ?? false)
  const [referrals, setReferrals] = useState(consent?.referrals ?? false)

  useEffect(() => {
    const reopen = () => {
      const current = readConsent()
      setAnalytics(current?.analytics ?? false)
      setAds(current?.ads ?? false)
      setReferrals(current?.referrals ?? false)
      setReopened(true)
    }
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen)
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen)
  }, [])

  useEffect(() => {
    if (!consent || (!consent.analytics && !consent.ads) || excluded) return
    loadAnalytics(consent)
    if (consent.analytics) trackPageView(pathname)
    flushSignUp()
  }, [consent, excluded, pathname])

  // Asked again when the choice predates ad measurement (an outdated consent cookie).
  if (excluded || (consent !== null && !consent.outdated && !reopened)) return null

  const decide = (choice: CookieChoice) => {
    saveConsent(choice)
    stopAnalytics(choice)
    if (choice.referrals) applyPendingReferral()
    else stopReferrals()
    setConsent(choice)
    setReopened(false)
    setCustomizing(false)
  }

  // On a phone it stays a short strip, so the sign-up form stays in view: "Reject all" and
  // "Accept all" side by side and equal, "Customize" smaller below them.
  // On the sign-up and sign-in pages it takes its own space above the form on screens under 1024 px
  // wide, and sits bottom left, over the dark side panel and clear of the form, from 1024 px.
  return (
    <section
      aria-labelledby="cookie-notice-title"
      className={cn(
        'z-50 grid max-w-md gap-2.5 overflow-y-auto overscroll-contain rounded-xl border border-border bg-surface p-4 shadow-lg sm:gap-3 sm:p-5',
        onAuthPage
          ? 'relative mx-2 mt-2 sm:mx-auto lg:fixed lg:bottom-4 lg:left-6 lg:mx-0 lg:mt-0 lg:max-h-[calc(100dvh-2rem)]'
          : 'fixed inset-x-2 bottom-2 mx-auto max-h-[calc(100dvh-1rem)] sm:inset-x-4 sm:right-6 sm:bottom-4 sm:left-auto sm:mx-0',
      )}
    >
      <h2 id="cookie-notice-title" className="display text-base text-ink sm:text-xl">
        Cookies, your choice
      </h2>
      <p className="text-[13px] leading-relaxed text-ink-muted">
        {/* Short on purpose (owner, 2026-10-20): the purposes here, the details under Customize. */}
        With your OK, we use cookies for analytics, to measure our ads and for partner referrals. It covers
        arvaseo.com too; change it any time in Cookie settings.{' '}
        <a
          href="https://arvaseo.com/privacy/#cookies"
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-ink underline underline-offset-2"
        >
          Privacy Policy
        </a>
      </p>
      {customizing && (
        <fieldset className="grid gap-2.5 rounded-lg border border-border p-3 text-[13px]">
          <legend className="sr-only">Cookies you allow</legend>
          <Choice
            label="Necessary"
            description="Keeps you signed in, keeps your requests secure and remembers this choice. Always on."
            checked
            disabled
          />
          <Choice
            label="Analytics"
            description="Google Analytics shows us which pages people use, so we can improve them."
            checked={analytics}
            onChange={setAnalytics}
          />
          <Choice
            label="Ad measurement"
            description="Google Ads and Meta (Facebook and Instagram) measure which of our ads bring visitors and sign-ups, and may show our ads to people who visited."
            checked={ads}
            onChange={setAds}
          />
          <Choice
            label="Partner referrals"
            description="If a partner's link sent you, we remember their code for 30 days so they're credited if you sign up and subscribe."
            checked={referrals}
            onChange={setReferrals}
          />
        </fieldset>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Button
          variant="secondary"
          className="order-1"
          onClick={() => decide({ analytics: false, ads: false, referrals: false })}
        >
          Reject all
        </Button>
        <Button
          variant="ghost"
          size={customizing ? 'md' : 'sm'}
          className="order-3 col-span-2 sm:order-2 sm:col-span-1 sm:h-10"
          onClick={() => (customizing ? decide({ analytics, ads, referrals }) : setCustomizing(true))}
        >
          {customizing ? 'Save choices' : 'Customize'}
        </Button>
        <Button
          variant="secondary"
          className="order-2 sm:order-3"
          onClick={() => decide({ analytics: true, ads: true, referrals: true })}
        >
          Accept all
        </Button>
      </div>
    </section>
  )
}

function Choice({
  label,
  description,
  checked,
  disabled = false,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
}) {
  return (
    <label className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5">
      <input
        type="checkbox"
        className="mt-0.5 size-4 accent-current"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="grid gap-0.5">
        <span className="font-semibold text-ink">{label}</span>
        <span className="text-xs leading-relaxed text-ink-muted">{description}</span>
      </span>
    </label>
  )
}
